import TagManager from 'react-gtm-module'
import type { ICart } from '~/schemas/cart/cart'

// Definindo as interfaces para os dados de e-commerce
interface EcommerceItem {
    item_name?: string
    item_id?: string
    price: number
    [key: string]: any
}

interface EcommerceEvent {
    event: string
    ecommerce: {
        items: EcommerceItem[]
        [key: string]: any
    }
}

const useGa4DataLayer = () => {
    const pushToDataLayer = (eventData: EcommerceEvent) => {
        TagManager.dataLayer({
            dataLayer: eventData,
        })
    }

    const ga4Event = {
        viewItem: (items: EcommerceItem[]) => {
            pushToDataLayer({
                event: 'view_item',
                ecommerce: { items },
            })
        },

        viewCart: (cart?: ICart) => {
            if (!cart) return

            const items: EcommerceItem[] = cart.cartItems.map((item) => ({
                item_id: item.product.sku,
                item_name: item.product.name,
                item_category: item.product.categories,
                price: item.product.discountedPrice,
                quantity: item.quantity,
            }))
            pushToDataLayer({
                event: 'view_cart',
                ecommerce: {
                    currency: 'BRL',
                    value: items.reduce((acc, item) => acc + item.price * item.quantity, 0).toFixed(2),
                    items,
                },
            })
        },

        addItemToCart: (items?: EcommerceItem[]) => {
            if (!items) return

            pushToDataLayer({
                event: 'add_to_cart',
                ecommerce: { items },
            })
        },

        removeItemFromCart: (items: EcommerceItem[]) => {
            pushToDataLayer({
                event: 'remove_from_cart',
                ecommerce: { items },
            })
        },

        beginCheckout: (cart?: ICart | null, coupon?: string) => {
            if (!cart) return

            const items: EcommerceItem[] = cart.cartItems.map((item) => ({
                item_id: item.product.sku,
                item_name: item.product.name,
                item_category: item.product.categories,
                price: item.product.discountedPrice,
                quantity: item.quantity,
            }))

            pushToDataLayer({
                event: 'begin_checkout',
                ecommerce: { items, coupon: coupon || '', currency: 'BRL' },
            })
        },

        purchase: (
            cart: ICart | null | undefined,
            transactionId: string,
            value: number,
            currency: string,
            coupon?: string,
        ) => {
            if (!cart) return
            const items: EcommerceItem[] = cart.cartItems.map((item) => ({
                item_id: item.product.sku,
                item_name: item.product.name,
                item_category: item.product.categories,
                price: item.product.discountedPrice,
                quantity: item.quantity,
            }))

            pushToDataLayer({
                event: 'purchase',
                ecommerce: {
                    transaction_id: transactionId,
                    value,
                    currency,
                    coupon,
                    items,
                },
            })
        },

        refund: (transactionId: string, items: EcommerceItem[], value: number) => {
            pushToDataLayer({
                event: 'refund',
                ecommerce: {
                    transaction_id: transactionId,
                    value,
                    items,
                },
            })
        },

        addToWishlist: (items: EcommerceItem[]) => {
            pushToDataLayer({
                event: 'add_to_wishlist',
                ecommerce: { items },
            })
        },
    }

    return { ga4Event }
}

export default useGa4DataLayer
